import PictureImage from '../Common/PictureImage';
import { Fragment, useRef } from "react";
import { Link } from "react-router-dom";
import ContactForm from "../Common/ContactForm";
import { motion } from "framer-motion";
import { useInViewOnce } from "../../hooks/useInViewOnce";
import Footer from "../Layouts/Footer";
import FAQSection from "../FAQSection";
import RelatedLinks from "../Common/RelatedLinks";
import { customerReviews } from "../../data/customerReviews";
import { getRouteFaqs } from "../../data/pageRouteFaqs";
import "../Homepage/FAQ.css";
import "./layout.css";

/* ── TOKENS ─────────────────────────────────────────────────── */
const C = {
  navy:        "#1e3a8a",
  navyMid:     "#172d6e",
  navyDeep:    "#0f1f4d",
  navyCard:    "#162348",
  navySurface: "#1a2d5a",
  orange:      "#f97316",
  orangeDim:   "rgba(249,115,22,0.12)",
  orangeBorder:"rgba(249,115,22,0.3)",
  orangeGlow:  "rgba(249,115,22,0.18)",
  white:       "#f1f5ff",
  muted:       "#7b91c4",
  mutedLight:  "#a8bde0",
  ink:         "#0f172a",
  inkSoft:     "#475569",
  inkMuted:    "#64748b",
  page:        "#ffffff",
  panel:       "#ffffff",
  panelAlt:    "#f8fafc",
  line:        "#e2e8f0",
  // Keep a teal token for compatibility, but default to brand orange accents.
  teal:        "#f97316",
  tealDim:     "rgba(249,115,22,0.12)",
};

const HERO_BG_URL =
  "/amazonfba/prepcenterservices.webp";

const PRICING_PAGE_PATH = "/pricing-fulfilment-services";
const CONTACT_PAGE_PATH = "/contact-fulfillment-services";
const SERVICES_SECTION_ID = "prep-centre-services";

/* ── GLOBAL CSS ──────────────────────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { background: ${C.page}; }
    button:focus-visible { outline: 2px solid ${C.orange}; outline-offset: 3px; }

    .prep-centre-inline-link {
      color: ${C.orange};
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .prep-centre-inline-link:hover { opacity: 0.85; }

    .prep-centre-body-copy p {
      font-family: 'DM Sans', sans-serif;
      font-size: clamp(0.95rem, 2vw, 1.05rem);
      color: ${C.inkSoft};
      line-height: 1.85;
      margin-bottom: 1.15rem;
    }
    .prep-centre-body-copy p:last-child { margin-bottom: 0; }

    .prep-centre-review-card {
      background: ${C.panelAlt};
      border: 1px solid ${C.line};
      border-radius: 14px;
      padding: 22px 20px;
      height: 100%;
    }

    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes floatDot {
      0%,100% { transform: translateY(0) scale(1); opacity: 0.6; }
      50%      { transform: translateY(-18px) scale(1.15); opacity: 1; }
    }
    @keyframes pulse-ring {
      0%   { box-shadow: 0 0 0 0 rgba(249,115,22,0.5); }
      70%  { box-shadow: 0 0 0 14px rgba(249,115,22,0); }
      100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
    }
    @keyframes lineGrow {
      from { width: 0; }
      to   { width: 100%; }
    }
    @keyframes badgePop {
      0%   { transform: scale(0.85); opacity: 0; }
      100% { transform: scale(1);    opacity: 1; }
    }

    .service-card {
      background: ${C.panel};
      border: 1px solid ${C.line};
      border-radius: 16px;
      padding: 28px 24px;
      cursor: default;
      transition: border-color 0.25s, box-shadow 0.25s, transform 0.22s, background 0.25s;
      position: relative;
      overflow: hidden;
    }
    .service-card::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at top right, rgba(249,115,22,0.07), transparent 60%);
      opacity: 0;
      transition: opacity 0.3s;
      pointer-events: none;
    }
    .service-card:hover {
      border-color: ${C.orange};
      box-shadow: 0 0 0 1px ${C.orange}, 0 16px 36px rgba(15,23,42,0.08);
      transform: translateY(-5px);
      background: ${C.panelAlt};
    }
    .service-card:hover::before { opacity: 1; }

    .cta-primary {
      padding: 15px 36px;
      background: ${C.orange};
      border: none;
      border-radius: 12px;
      font-family: 'DM Sans', sans-serif;
      font-weight: 700;
      font-size: 1rem;
      color: #fff;
      cursor: pointer;
      letter-spacing: 0.02em;
      transition: opacity 0.2s, transform 0.18s;
      animation: pulse-ring 2.5s ease-in-out infinite;
    }
    .cta-primary:hover { opacity: 0.9; transform: scale(1.03); }

    a.cta-primary,
    a.cta-secondary {
      text-decoration: none;
      display: inline-block;
      text-align: center;
    }

    .hero-scroll-hint {
      text-decoration: none;
      color: inherit;
      cursor: pointer;
      transition: color 0.2s;
    }
    .hero-scroll-hint:hover { color: ${C.orange}; }

    .cta-secondary {
      padding: 15px 36px;
      background: transparent;
      border: 1.5px solid rgba(249,115,22,0.45);
      border-radius: 12px;
      font-family: 'DM Sans', sans-serif;
      font-weight: 700;
      font-size: 1rem;
      color: ${C.orange};
      cursor: pointer;
      letter-spacing: 0.02em;
      transition: background 0.2s, border-color 0.2s, transform 0.18s;
    }
    .cta-secondary:hover {
      background: ${C.orangeDim};
      border-color: ${C.orange};
      transform: scale(1.03);
    }

    @media (max-width: 768px) {
      .services-grid { grid-template-columns: 1fr !important; }
      .process-row {
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 28px 14px;
        align-items: start !important;
      }
      .process-arrow { display: none !important; }
      .process-step { width: 100%; min-width: 0; }
      .trust-grid    { grid-template-columns: 1fr !important; }
      .split-bottom  { flex-direction: column !important; }
    }
  `}</style>
);

/* ── SERVICE DATA ─────────────────────────────────────────────── */
const services = [
  { icon: "🏷️", title: "FNSKU Labelling",           desc: "FNSKU label application meeting Amazon placement requirements with guaranteed accuracy." },
  { icon: "🛍️", title: "Poly Bagging",               desc: "Compliant poly bagging with suffocation warnings for apparel, textiles, and soft goods." },
  { icon: "🫧", title: "Bubble Wrapping",             desc: "Protective bubble wrapping for fragile products, securely packed, sealed, and labelled for transit." },
  { icon: "🎁", title: "Kitting & Bundling",          desc: "Multi-SKU bundles assembled, labelled, and prepared as shipment-ready Amazon ASINs." },
  { icon: "📅", title: "Expiry Date Checks",          desc: "Expiry-date inspections ensure Amazon compliance, with non-compliant inventory quarantined before shipment preparation." },
  { icon: "❌", title: "Sticker Removal",             desc: "Retail labels and unwanted barcodes removed to meet Amazon compliance requirements." },
  { icon: "⚠️", title: "Suffocation Warning Labels",  desc: "Suffocation warning labels applied to meet Amazon poly bagging requirements." },
  { icon: "📋", title: "Box Content Labels",          desc: "Box content labels generated, printed, and applied to every Amazon FBA shipment carton." },
  { icon: "📦", title: "Oversized Item Prep",         desc: "Oversized item preparation with reinforced packaging, secure strapping, and Amazon-compliant labelling." },
  { icon: "🔒", title: "Fragile Item Prep",           desc: "Protective packaging solutions for fragile products, including foam inserts, corner guards, and double-boxing." },
  { icon: "📏", title: "Transparent Taping",          desc: "Manufacturer cartons sealed according to Amazon packaging requirements for apparel and boxed products." },
  { icon: "👟", title: "Shoe Box Prep",               desc: "Shoe boxes prepared to Amazon compliance standards with poly bagging, banding, and suffocation warning labels." },
];

type PrepService = {
  icon: string;
  title: string;
  desc: string;
};

/* ── PROCESS STEPS ────────────────────────────────────────────── */
const steps = [
  { icon: "🚛", label: "Receive",  detail: "Pallets and parcels received, checked, and booked into inventory on the same day." },
  { icon: "🔍", label: "Inspect",  detail: "Inventory inspection covering product condition, expiry dates, and Amazon compliance requirements." },
  { icon: "⚙️", label: "Prep",     detail: "Amazon FBA prep services completed with labelling, poly bagging, and protective packaging." },
  { icon: "✈️", label: "Ship",     detail: "Inventory dispatched to Amazon fulfilment centres within agreed service-level timeframes." },
];

/* ── TRUST BADGES ─────────────────────────────────────────────── */
const badges = [
  { icon: "✅", text: "Amazon-Compliant Preparation" },
  { icon: "⚡", text: "48-Hour Prep Turnaround"  },
  { icon: "🎯", text: "99.97% Labelling Accuracy" },
  { icon: "📦", text: "10,000+ Units Monthly"  },
  { icon: "🔁", text: "No Minimum Commitment"  },
  { icon: "💬", text: "Dedicated Account Manager"  },
];

/* ── SERVICE CARD COMPONENT ───────────────────────────────────── */
function ServiceCard({ svc, index }: { svc: PrepService; index: number }) {
  const ref  = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="service-card"
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: (index % 6) * 0.08, duration: 0.45, ease: "easeOut" }}
    >
      {/* corner accent */}
      <div style={{
        position: "absolute", top: 0, right: 0,
        width: 60, height: 60,
        borderBottom: `1px solid rgba(249,115,22,0.12)`,
        borderLeft:   `1px solid rgba(249,115,22,0.12)`,
        borderBottomLeftRadius: 12,
        pointerEvents: "none",
      }} />

      <div style={{
        width: 48, height: 48, borderRadius: 12,
        background: C.orangeDim,
        border: `1px solid ${C.orangeBorder}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "1.4rem", marginBottom: 16,
      }}>
        {svc.icon}
      </div>

      <h3 style={{
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 700, fontSize: "1.02rem",
        color: C.ink, marginBottom: 8, lineHeight: 1.3,
      }}>
        {svc.title}
      </h3>
      <p style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "0.875rem", color: C.inkSoft, lineHeight: 1.7,
      }}>
        {svc.desc}
      </p>

      {/* bottom teal pip */}
      <div style={{
        position: "absolute", bottom: 0, left: 24,
        width: 32, height: 2, borderRadius: 1,
        background: C.teal, opacity: 0.35,
      }} />
    </motion.div>
  );
}



const prepCentreTestimonials = customerReviews.filter((r) =>
  ['Jack', 'Sohail Wahid', 'Amanda Willaton'].includes(r.name)
);

/* ── WHY UK SELLERS ───────────────────────────────────────────── */
function WhyUkSellersSection() {
  const ref = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-50px" });

  return (
    <section
      ref={ref}
      aria-labelledby="prep-centre-why-uk-title"
      style={{
        padding: "80px 24px",
        background: C.panelAlt,
        borderTop: `1px solid ${C.line}`,
      }}
    >
      
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.h2
            id="prep-centre-why-uk-title"
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.5rem,3vw,2.1rem)",
              fontWeight: 800,
              color: C.ink,
              marginBottom: 40,
              lineHeight: 1.25,
              textAlign: "center",
            }}
          >
            Why UK Amazon Sellers Tour Our{" "}
            <span style={{ color: C.orange }}>FBA Prep Facility</span>
          </motion.h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '40px', alignItems: 'start' }}>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.12, duration: 0.5 }}
            >
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <li style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ color: C.orange, fontSize: '1.4rem', lineHeight: 1 }}>✓</span>
                  <div>
                    <strong style={{ color: C.ink, display: 'block', marginBottom: '4px', fontSize: '1.1rem' }}>Remove Operational Bottlenecks</strong>
                    <span style={{ color: C.inkSoft, fontSize: '0.95rem', lineHeight: 1.6 }}>Stop stalling growth with unlabelled stock and Amazon check-in failures. Keep your replenishment windows open.</span>
                  </div>
                </li>
                <li style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ color: C.orange, fontSize: '1.4rem', lineHeight: 1 }}>✓</span>
                  <div>
                    <strong style={{ color: C.ink, display: 'block', marginBottom: '4px', fontSize: '1.1rem' }}>48-Hour Prep SLA</strong>
                    <span style={{ color: C.inkSoft, fontSize: '0.95rem', lineHeight: 1.6 }}>Fast workflow targets 48 hours from booked-in stock to dispatch-ready shipments. Cash flow demands speed.</span>
                  </div>
                </li>
                <li style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ color: C.orange, fontSize: '1.4rem', lineHeight: 1 }}>✓</span>
                  <div>
                    <strong style={{ color: C.ink, display: 'block', marginBottom: '4px', fontSize: '1.1rem' }}>Focus on Margins</strong>
                    <span style={{ color: C.inkSoft, fontSize: '0.95rem', lineHeight: 1.6 }}>Let your team focus on sourcing and listings while we handle the tape guns and spreadsheet fixes.</span>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.5 }}
              style={{ background: '#ffffff', padding: '24px', borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)', border: `1px solid ${C.line}` }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '1.4rem', color: C.ink, fontWeight: 'bold' }}>Discuss Your FBA Prep Needs</h3>
              <ContactForm source="amazon-fba-prep-centre-services" isFbaPrep={true} />
            </motion.div>
          </div>
        </div>
      </section>
  );
}

/* ── TESTIMONIALS ─────────────────────────────────────────────── */
function PrepCentreTestimonialsSection() {
  const ref = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-50px" });

  return (
    <section
      ref={ref}
      aria-labelledby="prep-centre-reviews-title"
      style={{ padding: "72px 24px", background: C.page, borderTop: `1px solid ${C.line}` }}
    >
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <motion.h2
          id="prep-centre-reviews-title"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(1.45rem,3vw,2rem)",
            fontWeight: 800,
            color: C.ink,
            textAlign: "center",
            marginBottom: 10,
          }}
        >
          Trusted by <span style={{ color: C.orange }}>UK Amazon Sellers</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.1 }}
          style={{
            textAlign: "center",
            color: C.inkMuted,
            marginBottom: 40,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.95rem",
          }}
        >
          99.97% labelling accuracy · 48-hour prep SLA · 10,000+ units prepped monthly
        </motion.p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 18,
          }}
        >
          {prepCentreTestimonials.map((review, i) => (
            <motion.blockquote
              key={review.name}
              className="prep-centre-review-card"
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.08 * i, duration: 0.4 }}
            >
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.92rem",
                  color: C.inkSoft,
                  lineHeight: 1.7,
                  marginBottom: 16,
                }}
              >
                &ldquo;{review.text}&rdquo;
              </p>
              <footer
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  color: C.ink,
                }}
              >
                {review.name}
                <span style={{ fontWeight: 500, color: C.inkMuted }}> · {review.company}</span>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── PROCESS SECTION ──────────────────────────────────────────── */
function ProcessSection() {
  const ref    = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-60px" });

  return (
    <section ref={ref} style={{
      background: C.page,
      borderTop:    `1px solid ${C.line}`,
      borderBottom: `1px solid ${C.line}`,
      padding: "72px 24px",
    }}>
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <motion.h2
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(1.6rem,3vw,2.2rem)", fontWeight: 800,
            color: C.ink, textAlign: "center", marginBottom: 10,
          }}
        >
          How Your Stock Gets <span style={{ color: C.orange }}>Amazon-Ready</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.15 }}
          style={{ textAlign: "center", color: C.inkMuted, marginBottom: 56, fontFamily: "'DM Sans',sans-serif" }}
        >
          Four-step Amazon FBA prep process delivering complete compliance, accuracy, and shipment readiness.
        </motion.p>

        <div className="process-row" style={{
          display: "flex", alignItems: "center",
          gap: 0, position: "relative",
        }}>
          {steps.map((step, i) => (
            <Fragment key={step.label}>
              <motion.div
                className="process-step"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.1 + i * 0.13, duration: 0.4 }}
                style={{ flex: 1, textAlign: "center", minWidth: 0 }}
              >
                <div style={{
                  width: 64, height: 64, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${C.orange}, #ea6000)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.6rem", margin: "0 auto 14px",
                  boxShadow: `0 0 0 8px rgba(249,115,22,0.1)`,
                }}>
                  {step.icon}
                </div>
                <div style={{
                  fontFamily: "'Playfair Display',serif",
                  fontWeight: 700, fontSize: "1.05rem", color: C.ink, marginBottom: 5,
                }}>
                  {step.label}
                </div>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: C.inkSoft, lineHeight: 1.5, padding: "0 8px" }}>
                  {step.detail}
                </div>
              </motion.div>

              {i < steps.length - 1 && (
                <motion.div
                  className="process-arrow"
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.25 + i * 0.13 }}
                  style={{
                    flex: "0 0 auto", margin: "0 8px",
                    color: C.orange, fontSize: "1.4rem",
                    paddingBottom: 28,
                  }}
                  aria-hidden="true"
                >
                  →
                </motion.div>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── TRUST BADGES ─────────────────────────────────────────────── */
function TrustSection() {
  const ref    = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} style={{ padding: "72px 24px", background: C.page }}>
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: C.orangeDim, border: `1px solid ${C.orangeBorder}`,
            borderRadius: 999, padding: "7px 20px",
            marginBottom: 16,
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.82rem", fontWeight: 700,
            color: C.orange, letterSpacing: "0.08em", textTransform: "uppercase",
            width: "fit-content", margin: "0 auto 16px",
          }}
        >
          ✦ Trust & Standards ✦
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 800,
            color: C.ink, textAlign: "center", marginBottom: 10,
          }}
        >
          All Prep Done to Amazon's{" "}
          <span style={{ color: C.orange }}>Exact Standards</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.18 }}
          style={{ textAlign: "center", color: C.inkSoft, marginBottom: 48, fontFamily: "'DM Sans',sans-serif", fontSize: "0.95rem" }}
        >
          Every Amazon FBA prep service follows Seller Central requirements, preventing rework, compliance issues, and check-in delays.
        </motion.p>

        <div className="trust-grid" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 16,
        }}>
          {badges.map((b, i) => (
            <motion.div
              key={b.text}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.08 * i, duration: 0.4 }}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                background: C.panelAlt,
                border: `1px solid ${C.line}`,
                borderRadius: 12, padding: "18px 20px",
                transition: "border-color 0.2s",
              }}
              whileHover={{ borderColor: C.orange }}
            >
              <span style={{
                width: 40, height: 40, borderRadius: 10,
                background: C.orangeDim,
                border: `1px solid ${C.orangeBorder}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.15rem", flexShrink: 0,
              }}>{b.icon}</span>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: C.ink, fontSize: "0.95rem" }}>
                {b.text}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── PRICING NOTE ─────────────────────────────────────────────── */
function PricingNote() {
  const ref    = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-60px" });

  return (
    <section ref={ref} style={{
      background: C.page,
      padding: "80px 24px",
      borderTop: `1px solid ${C.line}`,
    }}>
      <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
        {/* Decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.7 }}
          style={{
            width: 60, height: 3, borderRadius: 2,
            background: C.orange, margin: "0 auto 32px",
            transformOrigin: "center",
          }}
        />

        <motion.h2
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.5 }}
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: "clamp(1.7rem,3.5vw,2.6rem)", fontWeight: 900,
            color: C.ink, marginBottom: 18, lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          Transparent Per-Unit Pricing
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.22 }}
          style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "clamp(0.95rem,2vw,1.1rem)",
            color: C.inkSoft, lineHeight: 1.8, marginBottom: 44,
          }}
        >
          Transparent per-unit pricing with no setup fees, minimum charges, or hidden costs. Volume discounts apply automatically as your Amazon FBA prep requirements grow.
        </motion.p>

        {/* Pricing pills */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.32 }}
          style={{
            display: "flex", flexWrap: "wrap",
            justifyContent: "center", gap: 12, marginBottom: 48,
          }}
        >
          {["FNSKU Labels From £0.08", "Poly Bagging From £0.12", "Bubble Wrapping From £0.15", "Kitting Services From £0.25"].map((item) => (
            <span key={item} style={{
              background: C.panelAlt,
              border: `1px solid ${C.orangeBorder}`,
              borderRadius: 999, padding: "8px 20px",
              fontFamily: "'DM Sans',sans-serif",
              fontSize: "0.85rem", fontWeight: 600,
              color: C.orange, letterSpacing: "0.02em",
            }}>
              {item}
            </span>
          ))}
        </motion.div>

        {/* CTA row */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.42 }}
          style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}
        >
          <Link to={PRICING_PAGE_PATH} className="cta-primary">View Full Pricing →</Link>
          <Link to={CONTACT_PAGE_PATH} className="cta-secondary">Sign Up Today</Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ── HERO ─────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="hero-section fba-page-hero" style={{ textAlign: "left" }}>
      <PictureImage
        className="fba-page-hero__bg"
        src={HERO_BG_URL}
        alt="Amazon FBA prep centre services UK warehouse interior"
        width={1600}
        height={900}
        decoding="async"
        isHero
      />
      <div className="fba-page-hero__overlay" aria-hidden />

      {/* Dot grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `radial-gradient(circle, rgba(249,115,22,0.18) 1px, transparent 1px)`,
        backgroundSize: "36px 36px",
        maskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, black 30%, transparent 100%)",
        zIndex: 2,
      }} />

      {/* Ambient glow */}
      <div style={{
        position: "absolute", top: -80, left: "50%",
        transform: "translateX(-50%)",
        width: 600, height: 300, borderRadius: "50%",
        background: "radial-gradient(ellipse, rgba(249,115,22,0.12) 0%, transparent 70%)",
        pointerEvents: "none",
        zIndex: 2,
      }} />

      {/* Floating decorative dots */}
      {[
        { top: "18%", left: "8%",  size: 8,  delay: "0s",    color: C.orange },
        { top: "60%", left: "5%",  size: 5,  delay: "0.6s",  color: C.orange  },
        { top: "25%", right: "9%", size: 10, delay: "0.3s",  color: C.orange },
        { top: "70%", right: "6%", size: 6,  delay: "0.9s",  color: C.orange  },
      ].map((d, i) => (
        <div key={i} style={{
          position: "absolute", ...d,
          width: d.size, height: d.size, borderRadius: "50%",
          background: d.color, opacity: 0.55,
          animation: `floatDot 4s ${d.delay} ease-in-out infinite`,
          pointerEvents: "none",
          zIndex: 2,
        }} />
      ))}

      <div className="fba-page-hero__content">

      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: C.orangeDim, border: `1px solid ${C.orangeBorder}`,
          borderRadius: 999, padding: "7px 20px",
          fontFamily: "'DM Sans',sans-serif",
          fontSize: "0.78rem", fontWeight: 700,
          color: C.orange, letterSpacing: "0.1em", textTransform: "uppercase",
          marginBottom: 28,
          position: "relative",
          zIndex: 3,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.orange, display: "inline-block" }} />
        Facility tour · Milton Keynes
      </motion.div>

      {/* Headline */}
      <motion.h1
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.65, ease: "easeOut" }}
        style={{
          fontFamily: "'Playfair Display', serif",
          lineHeight: 1.1, letterSpacing: "-0.025em",
          color: C.white, marginBottom: 22,
          maxWidth: "min(800px, 100%)", margin: "0 0 22px",
          position: "relative",
          zIndex: 3,
        }}
      >
        Our warehouses in UK for FBA Prep
      </motion.h1>

      {/* Subheadline */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.28, duration: 0.5 }}
        style={{
          fontFamily: "'DM Sans', sans-serif",
          color: C.mutedLight, maxWidth: 580,
          margin: "0 0 40px", lineHeight: 1.75,
          position: "relative",
          zIndex: 3,
        }}
      >
        See how stock moves through our Milton Keynes facility — receiving, QA benches, labelling lines and dispatch — as part of our{' '}
        <Link to="/amazon-fba-prep-services" className="prep-centre-inline-link">Amazon FBA prep service UK</Link>.
        Compare <Link to="/amazon-fba-prep-pricing" className="prep-centre-inline-link">FBA prep pricing</Link> or return to the main prep hub for service scope.
      </motion.p>

      {/* Hero CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.42 }}
        style={{ display: "flex", gap: 14, justifyContent: "flex-start", flexWrap: "wrap", position: "relative", zIndex: 3 }}
      >
        <Link to={PRICING_PAGE_PATH} className="cta-primary">View Full Pricing →</Link>
        <Link to={CONTACT_PAGE_PATH} className="cta-secondary">Sign Up Today</Link>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        style={{
          marginTop: 56,
          position: "relative",
          zIndex: 3,
        }}
      >
        <a href={`#${SERVICES_SECTION_ID}`} className="hero-scroll-hint" style={{
          color: C.muted,
          fontFamily: "'DM Sans',sans-serif",
          fontSize: "0.8rem",
          letterSpacing: "0.08em",
        }}>
          ↓ EXPLORE SERVICES
        </a>
      </motion.div>
      </div>
    </section>
  );
}

/* ── SERVICES GRID ────────────────────────────────────────────── */
function ServicesGrid() {
  const ref    = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-40px" });

  return (
    <section id={SERVICES_SECTION_ID} ref={ref} style={{
      padding: "80px 24px",
      background: C.page,
      borderTop: `1px solid ${C.line}`,
    }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", flexWrap: "wrap",
            gap: 12, marginBottom: 44,
          }}
        >
          <div>
            <p style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem",
              fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
              color: C.orange, marginBottom: 6,
            }}>Our Services</p>
            <h2 style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 800,
              color: C.ink, lineHeight: 1.2,
            }}>
              12 Specialist Prep Services
            </h2>
          </div>
          <span style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.85rem", color: C.inkSoft,
          }}>
            All services available individually or combined
          </span>
        </motion.div>

        <div
          className="services-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 20,
          }}
        >
          {services.map((svc, i) => (
            <ServiceCard key={svc.title} svc={svc} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── ROOT ─────────────────────────────────────────────────────── */
export default function PrepCentreServices() {
  const prepCentreFaqs = getRouteFaqs('/amazon-fba-prep-centre-services');

  return (
    <>
      <div style={{ fontFamily: "'DM Sans', sans-serif", background: C.page, minHeight: "100vh", color: C.ink }}>
        <GlobalStyle />
        <Hero />

      {/* AEO direct-answer section */}
      <section
        className="fba-aeo-section"
        aria-labelledby="fba-aeo-title"
        style={{ background: '#f8fafc', padding: '4rem 2rem' }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto', background: '#ffffff', padding: '3rem', borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)', borderLeft: '5px solid #f97316' }}>
          <h2 id="fba-aeo-title" style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1a202c', marginBottom: '1.5rem' }}>
            What is an Amazon FBA Prep Centre?
          </h2>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#4a5568', marginBottom: '1.5rem' }}>
            <strong>Summary:</strong> An Amazon FBA prep centre is a specialised warehouse facility that receives your bulk inventory, inspects it for quality, applies mandatory FNSKU barcodes, and prepares items according to strict Amazon FBA guidelines (like polybagging and bubble wrapping) before shipping them directly to Amazon fulfilment centres. Pick Pack Pro operates a dedicated FBA prep facility in Milton Keynes, UK.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#4a5568', marginBottom: '1.5rem' }}>
            <strong>Audience & Use-Case:</strong> This facility is designed for UK Amazon Private Label brands, Wholesale sellers, and international merchants who need a reliable UK partner to handle complex physical prep workflows. By outsourcing to our facility, you eliminate the need to hire warehouse staff or manage compliance rules, allowing you to focus purely on sourcing and listing optimisation.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#4a5568', margin: 0 }}>
            <strong>Experience & Standards:</strong> We process thousands of FBA units daily. Every item is barcode-scanned and checked against live Amazon routing requirements to ensure zero check-in delays when your stock arrives at Amazon.
          </p>
        </div>
      </section>

        <ServicesGrid />
        <WhyUkSellersSection />
        <ProcessSection />
        <TrustSection />
        <PrepCentreTestimonialsSection />
        <PricingNote />
        <FAQSection
          headingId="prep-centre-faq"
          className="faqs-section"
          title="Frequently Asked Questions"
          description="Quick answers on UK FBA prep pricing, turnaround, minimums and what is included in our prep centre service."
          faqs={prepCentreFaqs}
          schemaScriptId="prep-centre-faq-schema"
        />
        <RelatedLinks
          title="Related Services"
          serviceLinks={[
            {
              to: '/amazon-fba-prep-services',
              label: 'Amazon FBA prep service UK (main hub)',
            },
            {
              to: '/amazon-fba-shipments',
              label: 'Amazon FBA shipment planning and FC dispatch',
            },
            {
              to: '/amazon-fba-prep-pricing',
              label: 'Amazon FBA prep cost & pricing UK',
            },
            {
              to: '/amazon-fba-prep-private-label',
              label: 'Private label FBA prep for branded inventory',
            },
          ]}
        />
      </div>
      <Footer />
    </>
  );
}
